import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  despachoFiscalInfoApi,
  FORBIDDEN_FISCAL_INFO_MESSAGE,
  LOAD_FISCAL_DETAIL_ERROR,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  csfSyncAppliedCount,
  shouldAutoSyncCsfDetail,
} from "@/modules/despacho/despachoFiscalInfoHelpers";
import FiscalObligationsPanel from "@/pages/despacho/FiscalObligationsPanel";

/**
 * Pestaña Obligaciones fiscales en Editar cliente — reutiliza API despacho fiscal-info.
 * No toca Robot SAT, descarga automática ni sync masivo del módulo Despacho.
 */
export default function ClientFiscalObligationsTabPanel({
  clientId,
  readOnly = false,
  onUpdated,
}) {
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");

  const loadCatalog = useCallback(async () => {
    try {
      const catalogItems = await despachoFiscalInfoApi.listCatalog();
      setCatalog(catalogItems);
    } catch {
      setCatalog([]);
    }
  }, []);

  const applyDetailResponse = useCallback((data, { silent = false } = {}) => {
    setDetail(data);
    const added = csfSyncAppliedCount(data?.auto_sync_csf);
    if (!silent && added > 0) {
      toast.success(`${added} obligación(es) activada(s) desde la CFS.`);
      onUpdated?.();
    }
  }, [onUpdated]);

  const loadDetail = useCallback(async ({ silent = false, skipAutoSync = false } = {}) => {
    if (!clientId) return null;
    if (!silent) {
      setLoadState("loading");
      setLoadMessage("");
    }
    try {
      let data = await despachoFiscalInfoApi.getClientDetail(clientId, { autoSyncCsf: false });
      if (!readOnly && !skipAutoSync && shouldAutoSyncCsfDetail(data)) {
        try {
          data = await despachoFiscalInfoApi.getClientDetail(clientId, { autoSyncCsf: true });
          const applied = csfSyncAppliedCount(data?.auto_sync_csf);
          if (!silent && applied > 0) {
            toast.success(`${applied} obligación(es) activada(s) desde la CFS almacenada.`);
            onUpdated?.();
          }
        } catch (syncErr) {
          if (!silent) {
            toast.message("Detalle cargado; no se pudo sincronizar la CFS automáticamente.", {
              description: parseFiscalInfoError(syncErr).message.slice(0, 200),
            });
          }
        }
      }
      applyDetailResponse(data, { silent });
      if (!silent) setLoadState("ready");
      loadCatalog();
      return data;
    } catch (err) {
      const parsed = parseFiscalInfoError(err, { fallbackMessage: LOAD_FISCAL_DETAIL_ERROR });
      if (silent) {
        toast.error(parsed.message);
        return null;
      }
      setDetail(null);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
      return null;
    }
  }, [clientId, readOnly, onUpdated, loadCatalog, applyDetailResponse]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6" data-testid="client-obligaciones-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando obligaciones fiscales…
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div
        className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        role="alert"
        data-testid="client-obligaciones-forbidden"
      >
        {loadMessage || FORBIDDEN_FISCAL_INFO_MESSAGE}
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="space-y-3" data-testid="client-obligaciones-error">
        <p className="text-sm text-rose-800 m-0">{loadMessage || LOAD_FISCAL_DETAIL_ERROR}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="client-obligaciones-panel">
      <p className="text-xs text-slate-600 m-0">
        Catálogo de obligaciones del expediente fiscal. La constancia (CFS) se gestiona en la pestaña{" "}
        <strong>Constancia fiscal</strong>; el seguimiento de vencidos está en{" "}
        <strong>Tablero operativo</strong>. Si acaba de subir o reemplazar la CFS, use{" "}
        <strong>Recalcular desde constancia</strong> para actualizar las obligaciones.
      </p>
      <FiscalObligationsPanel
        clientId={clientId}
        detail={detail}
        catalog={catalog}
        readOnly={readOnly}
        onReload={() => loadDetail({ silent: true, skipAutoSync: true })}
        onUpdated={() => {
          onUpdated?.();
          loadDetail({ silent: true, skipAutoSync: true });
        }}
      />
    </div>
  );
}
